import * as chrono from 'chrono-node'
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  isSaturday,
  isWeekend,
  nextSaturday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

/**
 * A due-date range (design 31b): a start date plus the due date, both at day
 * precision (local midnight). Times are applied by the picker on save.
 */
export interface DayRange {
  start: Date
  due: Date
}

export type RangePresetKey =
  'this_week' | 'next_week' | 'next_2_weeks' | 'this_month'

export interface RangePreset {
  key: RangePresetKey
  range: DayRange
}

// Range presets and typed weeks use Monday weeks, like the single-date "Next week" preset.
const WEEK_STARTS_ON = 1

export const isSameCalendarDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/** Orders two days so start <= due (picking a start after the due swaps them). */
export function orderRange(a: Date, b: Date): DayRange {
  const first = startOfDay(a)
  const second = startOfDay(b)
  return first.getTime() <= second.getTime()
    ? { start: first, due: second }
    : { start: second, due: first }
}

/** True when a start and due fall on different days, i.e. the task carries a range. */
export function isMultiDayRange(
  startDate?: Date | null,
  dueDate?: Date | null,
): boolean {
  return !!startDate && !!dueDate && !isSameCalendarDay(startDate, dueDate)
}

export function getRangePresets(today: Date = new Date()): Array<RangePreset> {
  const opts = { weekStartsOn: WEEK_STARTS_ON } as const
  const nextWeekStart = addWeeks(startOfWeek(today, opts), 1)
  return [
    {
      key: 'this_week',
      range: {
        start: startOfWeek(today, opts),
        due: startOfDay(endOfWeek(today, opts)),
      },
    },
    {
      key: 'next_week',
      range: {
        start: nextWeekStart,
        due: startOfDay(endOfWeek(nextWeekStart, opts)),
      },
    },
    {
      key: 'next_2_weeks',
      range: { start: nextWeekStart, due: addDays(nextWeekStart, 13) },
    },
    {
      key: 'this_month',
      range: { start: startOfMonth(today), due: startOfDay(endOfMonth(today)) },
    },
  ]
}

/** Calendar days in the range, both ends included ("Sep 28 → Oct 2" is 5). */
export function countRangeDays(range: DayRange): number {
  return differenceInCalendarDays(range.due, range.start) + 1
}

/** Monday to Friday days in the range, both ends included. */
export function countWorkingDays(range: DayRange): number {
  const days = countRangeDays(range)
  if (days <= 0) return 0
  const fullWeeks = Math.floor(days / 7)
  let count = fullWeeks * 5
  let cursor = addDays(startOfDay(range.start), fullWeeks * 7)
  for (let i = 0; i < days % 7; i++) {
    if (!isWeekend(cursor)) count++
    cursor = addDays(cursor, 1)
  }
  return count
}

/**
 * Compact range for the preset column: "Sep 28–Oct 4", "Oct 5–11" (en-US),
 * "28 Sept–4 Oct" (en-GB). Locale-aware via Intl, with the dash drawn tight.
 */
export function formatCompactRange(range: DayRange, locale?: string): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  })
  if (isSameCalendarDay(range.start, range.due))
    return formatter.format(range.start)
  return formatter
    .formatRange(range.start, range.due)
    .replace(/\s*[–-]\s*/, '–')
}

/** "Sep 28 → Oct 2": the range as it reads on the property row and create chip. */
export function formatArrowRange(
  start: Date,
  due: Date,
  formatDay: (date: Date) => string,
): string {
  return `${formatDay(start)} → ${formatDay(due)}`
}

const CONNECTOR =
  /[–—]|\s(?:to|until|till|thru|through)(?:\s|$)|\s-(?:\s|$)|[a-z]\s*-|-\s*[a-z]/i

/**
 * Typing "to" or "–" in the single-date search switches the picker to Range.
 * A plain ISO date ("2026-10-03") doesn't count: the dash sits between digits.
 */
export function looksLikeRangeQuery(query: string): boolean {
  const trimmed = query.trim()
  if (!trimmed) return false
  const match = CONNECTOR.exec(` ${trimmed}`)
  // Needs something typed before the connector ("mon to", not "to").
  return !!match && match.index > 0
}

const NUMBER_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
}

type Unit = 'day' | 'week' | 'month'

function span(start: Date, amount: number, unit: Unit): DayRange {
  const end =
    unit === 'day'
      ? addDays(start, amount)
      : unit === 'week'
        ? addWeeks(start, amount)
        : addMonths(start, amount)
  return { start, due: addDays(end, -1) }
}

function parseKeywordRange(query: string, today: Date): DayRange | null {
  const opts = { weekStartsOn: WEEK_STARTS_ON } as const
  const presets = getRangePresets(today)
  const byKey = (key: RangePresetKey) =>
    presets.find((p) => p.key === key)?.range ?? null

  switch (query) {
    case 'this week':
    case 'week':
      return byKey('this_week')
    case 'next week':
      return byKey('next_week')
    case 'this month':
    case 'month':
      return byKey('this_month')
    case 'next month': {
      const start = startOfMonth(addMonths(today, 1))
      return { start, due: startOfDay(endOfMonth(start)) }
    }
    case 'weekend':
    case 'this weekend': {
      const saturday = isSaturday(today)
        ? startOfDay(today)
        : startOfDay(nextSaturday(today))
      return { start: saturday, due: addDays(saturday, 1) }
    }
    case 'rest of the week':
    case 'rest of week':
      return {
        start: startOfDay(today),
        due: startOfDay(endOfWeek(today, opts)),
      }
    case 'rest of the month':
    case 'rest of month':
      return { start: startOfDay(today), due: startOfDay(endOfMonth(today)) }
    default:
      break
  }

  // "next 2 weeks", "next 3 days", "coming 2 months": the periods after this one,
  // matching the "Next week" / "Next 2 weeks" presets.
  const next =
    /^(?:the\s+)?(?:next|coming)\s+(\d+|a|an|one|two|three|four|five|six)\s+(day|week|month)s?$/.exec(
      query,
    )
  if (next) {
    const amount = Number(next[1]) || NUMBER_WORDS[next[1] ?? ''] || 1
    const unit = next[2] as Unit
    const start =
      unit === 'day'
        ? addDays(startOfDay(today), 1)
        : unit === 'week'
          ? addWeeks(startOfWeek(today, opts), 1)
          : startOfMonth(addMonths(today, 1))
    return span(start, amount, unit)
  }

  // "2 weeks", "10 days": that long, starting today.
  const bare = /^(\d+)\s+(day|week|month)s?$/.exec(query)
  if (bare) {
    return span(startOfDay(today), Number(bare[1]), bare[2] as Unit)
  }

  return null
}

/**
 * Parse a typed range: "mon to fri", "next 2 weeks", "sep 28 – oct 2",
 * "tomorrow to friday". Uses chrono (like the single-date search) for anything
 * with an explicit end. A lone date ("fri") reads as "today until then" when
 * `allowSingleDate` is set, so Range mode never dead-ends on a plain day.
 */
export function parseRangeQuery(
  query: string,
  today: Date = new Date(),
  { allowSingleDate = true }: { allowSingleDate?: boolean } = {},
): DayRange | null {
  const normalized = query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    // chrono reads the en dash and hyphen as a range connector, not the em dash.
    .replace(/—/g, '–')
    .replace(/\s(?:until|till|thru|through)\s/g, ' to ')
  if (!normalized) return null

  const keyword = parseKeywordRange(normalized, today)
  if (keyword) return keyword

  const [result] = chrono.parse(normalized, startOfDay(today), {
    // Day precision: from midnight, so "mon to fri" on a Monday evening starts today.
    forwardDate: true,
  })
  if (!result) return null

  const start = startOfDay(result.start.date())
  if (result.end) {
    let due = startOfDay(result.end.date())
    // "fri to mon" means the Monday after, not the one before.
    const endIsWeekdayOnly =
      result.end.isCertain('weekday') && !result.end.isCertain('day')
    while (endIsWeekdayOnly && due.getTime() < start.getTime()) {
      due = addWeeks(due, 1)
    }
    return orderRange(start, due)
  }

  if (!allowSingleDate) return null
  return orderRange(startOfDay(today), start)
}
